const DOCUSEAL_API_URL = process.env.REACT_APP_DOCUSEAL_URL;
const DOCUSEAL_API_KEY = process.env.REACT_APP_DOCUSEAL_KEY;

const userName = sessionStorage.getItem("username");

export async function submitDocument({
  file,
  fields,
  customer,
  referenceNumber,
  referenceType,
  documentType,
}) {
  if (!customer?.customerEmail) {
    throw new Error("Customer email is required");
  }
  if (!file) {
    throw new Error("PDF file is required");
  }

  if (!fields?.length) {
    throw new Error("At least one field is required");
  }
  const arrayBuffer = await file.arrayBuffer();
  const base64Pdf = btoa(
    new Uint8Array(arrayBuffer).reduce(
      (data, byte) => data + String.fromCharCode(byte),
      "",
    ),
  );
  // Expire 24 hours from now (ISO 8601, UTC)
  const expireAt = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();

  const payload = {
    name: file.name,
    documents: [
      {
        name: file.name,
        role: "Signer",
        file: base64Pdf,
        fields,
      },
    ],
    username: sessionStorage.getItem("username"),
    customerFirstName: customer.customerFirstName,
    customerLastName: customer.customerLastName,
    customerEmail: customer.customerEmail,
    customerPhone: customer.customerPhone,
    referenceNumber,
    referenceType,
    documentType,
    submission: {
      expire_at: expireAt,
      submitters: [
        {
          role: "Signer",
          email: customer.customerEmail,
          name: `${customer.customerFirstName} ${customer.customerLastName}`,
          phone: customer.customerPhone,
        },
      ],
    },
  };
  const res = await fetch(
    `${DOCUSEAL_API_URL}/documentSubmissions/pdf?include=fields`,
    {
      method: "POST",
      headers: {
        "X-Auth-Token": DOCUSEAL_API_KEY,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    },
  );

  const data = await res.json();

  if (!res.ok) {
    throw new Error(data?.error || "Document submission failed");
  }

  return data;
}

export async function fetchDocumentPdf(url) {
  if (!url) throw new Error("PDF url is required");

  // Try 1: with auth token (for protected DocuSeal endpoints)
  try {
    const res = await fetch(url, {
      headers: { "X-Auth-Token": DOCUSEAL_API_KEY },
    });
    if (res.ok) return await res.arrayBuffer();
  } catch (e) {
    console.warn("Auth-headed PDF fetch failed, retrying plain:", e);
  }

  // Try 2: plain fetch (for public/signed CDN URLs)
  const res = await fetch(url);
  if (!res.ok) throw new Error(`PDF fetch failed: HTTP ${res.status}`);
  return res.arrayBuffer();
}

export async function getSubmitter(submitterId) {
  if (!submitterId) throw new Error("submitterId is required");
  const response = await fetch(
    `${DOCUSEAL_API_URL}/submitters/${submitterId}?include=fields`,
    {
      headers: {
        "X-Auth-Token": DOCUSEAL_API_KEY,
        "Content-Type": "application/json",
      },
    },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err?.error || "Failed to fetch submitter");
  }
  return response.json();
}

export async function submitSignature({
  submitterId,
  values,
  completed = true,
}) {
  if (!submitterId) {
    throw new Error("submitterId is required");
  }
  const response = await fetch(
    `${DOCUSEAL_API_URL}/submitters/${submitterId}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Auth-Token": DOCUSEAL_API_KEY,
      },
      body: JSON.stringify({
        values,
        completed,
      }),
    },
  );
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err?.error || "Submitter submission failed");
  }
  return response.json();
}

export async function getAllDocument({
  page = 1,
  limit = 10,
  status = "ALL",
  search = "",
  username,
}) {
  const params = new URLSearchParams({
    page,
    limit,
    username: username || userName,
  });

  if (status !== "ALL") params.append("status", status);
  if (search) params.append("referenceNumber", search);
  params.append("include", "fields");

  const response = await fetch(
    `${DOCUSEAL_API_URL}/documentSubmissions/pdf?${params.toString()}`,
    {
      headers: {
        "X-Auth-Token": DOCUSEAL_API_KEY,
        "Content-Type": "application/json",
      },
    },
  );

  if (!response.ok) {
    const err = await response.json();
    throw new Error(err?.error || "Failed to fetch document submissions");
  }

  return response.json();
}

export async function sendMailLink({ to, token }) {
  const response = await fetch(`${DOCUSEAL_API_URL}/reviewandsigndocument`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Auth-Token": DOCUSEAL_API_KEY,
    },
    body: JSON.stringify({
      to,
      token,
    }),
  });

  if (!response.ok) {
    throw new Error(
      `Mail API failed: ${response.status} ${response.statusText}`,
    );
  }

  return response.text();
}
